import { useId, useMemo, type ReactNode } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";

import { RemovableTag } from "src/components/AssociatedStructures";
import { CompanyAutocomplete, type CompanyOption } from "src/components/Selector/CompanySelector/CompanyAutocomplete";
import { FormHelperBox } from "./FormHelperBox";
import { formatFieldLabel } from "./FoodsFieldPrimitives";

export type OrganizationIdentity = {
    "@id": string;
    id: number;
    name: string;
};

export type AssociatedOrganizationsFieldProps = {
    organizations: readonly OrganizationIdentity[];
    excludedOrganizationIds?: readonly number[];
    hierarchyRootId: number | undefined;
    label: ReactNode;
    helperText: ReactNode;
    placeholder: string;
    removeLabel: (organization: OrganizationIdentity) => string;
    onAdd: (organizations: CompanyOption[]) => void;
    onRemove: (organizationId: number) => void;
};

/** Shared visual field for selecting and removing associated organizations. */
export const AssociatedOrganizationsField = (props: AssociatedOrganizationsFieldProps) => {
    const {
        organizations,
        excludedOrganizationIds = [],
        hierarchyRootId,
        label,
        helperText,
        placeholder,
        removeLabel,
        onAdd,
        onRemove,
    } = props;
    const theme = useTheme();
    const generatedId = useId();
    const labelId = `associated-organizations-label-${generatedId}`;
    const displayedLabel = typeof label === "string" ? formatFieldLabel(label) : label;
    const excludeIds = useMemo(
        () => [...new Set([...excludedOrganizationIds, ...organizations.map((organization) => organization.id)])],
        [excludedOrganizationIds, organizations],
    );

    return (
        <Stack sx={{ gap: theme.spacing(2) }}>
            <Stack sx={{ gap: theme.spacing(1) }}>
                <Typography id={labelId} variant="body-semibold">
                    {displayedLabel}
                </Typography>
                <FormHelperBox>{helperText}</FormHelperBox>
            </Stack>

            {organizations.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: theme.spacing(1) }}>
                    {organizations.map((organization) => (
                        <RemovableTag
                            key={organization.id}
                            label={organization.name}
                            backgroundColor={theme.custom.grey[400]}
                            foregroundColor={theme.custom.grey[2500]}
                            removeLabel={removeLabel(organization)}
                            onRemove={() => onRemove(organization.id)}
                        />
                    ))}
                </Box>
            )}

            <CompanyAutocomplete
                value={[]}
                onChange={onAdd}
                appearance="product-v2"
                labelId={labelId}
                hierarchyRootId={hierarchyRootId}
                placeholder={placeholder}
                excludeIds={excludeIds}
            />
        </Stack>
    );
};
